import { Component, Inject, OnInit, Injector } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DialogData } from 'app/core/interfaces/dialog';
import { scheduleI, scheduleType } from 'app/core/interfaces/schedule';
import { Patient } from 'app/core/interfaces/patient';
import { Practitioner } from 'app/core/interfaces/practitioner';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { OrganizationInterface } from 'app/core/interfaces/organization';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { Healthcareservice } from 'app/core/interfaces/healthcareservice';
import { SecretaryInterface } from 'app/core/interfaces/secretary';
import { Encounter } from 'app/core/interfaces/encounter';
import { TimeSlot } from 'app/core/interfaces/practitionerHours';
import { Agreements, HealthcarePlans, PaymentMethod, PaymentSettings } from 'app/core/interfaces/payment-settings';
import { OrganizationSettingsService } from 'app/core/http/services/organization-settings';
import { PatientService } from 'app/core/http/services/patient.service';
import { HttpErrorResponse } from '@angular/common/http';
import { friendlyProcedureName } from 'app/modules/shared/models/schedule-search.models';

@Component({
  selector: 'page-schedule-registration',
  templateUrl: './schedule-registration.component.html',
  styleUrls: ['./schedule-registration.component.scss'],
})
export class PatientScheduleRegistrationComponent extends BaseComponent implements OnInit {
  private readonly defaultPaymentMethods: PaymentMethod[] = [
    { name: 'PIX', active: true, description: 'PIX' },
    { name: 'CREDIT_CARD', active: true, description: 'Cartão de Crédito' },
    { name: 'PHYSICAL', active: true, description: 'Presencial' },
  ];
  public schedules: scheduleI[] = [];
  public schedule: scheduleI = <scheduleI>{
    scheduling_method: 'PARTICULAR',
  };
  public practitioner: any = <any>{};
  public practitioner_id: string = '';
  public procedure: Healthcareservice = <Healthcareservice>{};
  public patients: Patient[] = [];
  public hours: any[] = [];

  public blockedDaysOfWeek = [0, 6];
  public type: scheduleType = 'Consulta';
  public title = '';
  public subtitles = ['Orientações', 'Localização'];
  public organization: any = <any>{};
  public openingHoursText = '';
  public id: number = 0;
  public isByOrder: boolean = false;
  public location: any = {};
  public locations: any[] = [];
  public address: any = {};
  public affilationsSecretary: any[] = [];
  public secretaries: SecretaryInterface[] = [];
  public secretariesAffiliation: any[] = [];
  public practitioners: Practitioner[] = [];
  public organizations: OrganizationInterface[] = [];
  public patientLogged: any = {};
  public submittedSchedule = false;
  public successSchedule = false;
  public isSavingSchedule = false;
  public dayOfWeekSelected: string = '';
  public weeklySchedule: any = {};
  public encounterPractitioner!: Encounter[];
  public encountersDone: any = {};
  public disabledTimes: any[] = [];
  public today = new Date();
  public slotsReturned: any[] = [];
  public submittedForm = false;
  public organizationSettings?: PaymentSettings;
  public paymentSettingsLoaded = false;
  public healthPlansOrganization: HealthcarePlans[] = [];
  public healPlanAgreement: Agreements[] = [];
  public paymentMethods: PaymentMethod[] = [...this.defaultPaymentMethods];
  public uploadedFiles: { name: string; base64: string }[] = [];
  public isDragging = false;
  public datetime: string = '';
  public patient!: Patient;
  public byOrderStart: string = '';

  constructor(
    public dialogRef: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private encounterService: EncounterService,
    private locationService: OrganizationLocationService,
    private organizationSettingsService: OrganizationSettingsService,
    private injector: Injector,
    private patientService: PatientService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getPatient();
    const userLogged = this.getUserLogged();

    this.patientLogged = {
      patient_id: userLogged.id,
      name: userLogged.name,
      email: userLogged?.email,
      birthDate: userLogged?.birthDate,
      phone: userLogged.phone,
    };

    this.type = this.data.param.type;

    if (this.data.param.procedure) {
      this.procedure = this.data.param.procedure;
      this.address = this.data.param.address;
      this.organization = this.procedure.organization;
      this.isByOrder = this.data.param.isByOrder;
      this.byOrderStart = this.data.param.byOrderStart;
    }

    if (this.data.param.practitioner) {
      this.practitioner = this.data.param.practitioner;
      this.practitioner_id = this.data.param.practitioner.practitioner_id;
    }

    if (this.data.param.datetime) {
      this.datetime = this.data.param.datetime;
    }

    this.title = 'Confirmar agendamento';
    this.schedule.paymentMethod = 'PHYSICAL';

    this.getOrganizationSettings();
  }

  getAddress(address: any): string {
    if (!address) return 'Endereço não informado';

    const street = [address.street, address.number].filter(Boolean).join(', ');
    const locality = [address.city, address.state].filter(Boolean).join('/');
    const parts = [street, address.complement, address.district, locality].filter(Boolean);

    return parts.join(' · ') || address.formattedAddress || 'Endereço não informado';
  }

  friendlyProcedureName(name?: string): string {
    return friendlyProcedureName(name);
  }

  formatPrice(value?: number | string | null): string {
    if (value === null || value === undefined || value === '') return 'Valor sob consulta';

    const amount = Number(value);
    if (!Number.isFinite(amount)) return `R$ ${value}`;

    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
  }

  getDayName(dayOfWeek: number): string {
    const daysOfWeek = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
    return daysOfWeek[dayOfWeek];
  }

  getOrganizationSettings() {
    const organizationId = this.schedule?.organization?.organization_id ?? this.organization.organization_id;

    this.organizationSettingsService.findByOrganization(organizationId).subscribe({
      next: (settings: PaymentSettings[]) => {
        this.organizationSettings = settings?.[0];
        this.paymentMethods = this.getEnabledPaymentMethods(this.organizationSettings?.payment_methods);
        this.ensureSelectedPaymentMethod();
        this.paymentSettingsLoaded = true;

        this.healthPlansOrganization = this.organizationSettings?.healthcare_plans ?? [];

        this.healthPlansOrganization = this.healthPlansOrganization.filter((healthcarePlan) => healthcarePlan.accepted == true);
      },
      error: (err) => {
        this.paymentMethods = this.getEnabledPaymentMethods();
        this.ensureSelectedPaymentMethod();
        this.paymentSettingsLoaded = true;
      },
    });
  }

  getEnabledPaymentMethods(paymentMethods: PaymentMethod[] = []): PaymentMethod[] {
    const enabledMethods = paymentMethods.filter((method) => method.active);
    const methodsByName = new Map<string, PaymentMethod>();
    [...enabledMethods, ...this.defaultPaymentMethods].forEach((method) => {
      methodsByName.set(method.name, { ...method, active: true });
    });
    return this.defaultPaymentMethods.map((method) => methodsByName.get(method.name) || method);
  }

  ensureSelectedPaymentMethod() {
    const currentPaymentMethod = this.schedule.paymentMethod;

    if (!currentPaymentMethod || !this.isPaymentMethodAvailable(currentPaymentMethod)) {
      this.schedule.paymentMethod = this.paymentMethods[0]?.name;
    }
  }

  isPaymentMethodAvailable(paymentMethod?: string): boolean {
    return this.paymentMethods.some((method) => method.name === paymentMethod);
  }

  get hasOnlinePaymentMethods(): boolean {
    return this.paymentMethods.some((method) => ['PIX', 'CREDIT_CARD'].includes(method.name));
  }

  getPaymentMethodLabel(paymentMethod: string): string {
    const labels: { [key: string]: string } = {
      PIX: 'PIX',
      PHYSICAL: 'Presencial',
      CREDIT_CARD: 'Cartão de Crédito',
    };

    return labels[paymentMethod] ?? paymentMethod;
  }

  canSaveSchedule(): boolean {
    const hasDatetime = !!this.datetime;
    const hasPaymentMethod = this.schedule.scheduling_method !== 'PARTICULAR' || this.isPaymentMethodAvailable(this.schedule.paymentMethod);

    return hasDatetime && hasPaymentMethod && !this.successSchedule && !this.isSavingSchedule;
  }

  setHealthPlanAgreements(healthPlanName?: string) {
    if (healthPlanName == undefined) this.healPlanAgreement = [];

    const healthcarePLan = this.healthPlansOrganization.find((healthcareplan: HealthcarePlans) => healthcareplan.name == healthPlanName);

    this.healPlanAgreement = healthcarePLan?.agreements!!;
  }

  getDayOfWeek(dia: string): number {
    const daysOfWeek: { [key: string]: number } = {
      Segunda: 1,
      Terca: 2,
      Quarta: 3,
      Quinta: 4,
      Sexta: 5,
      Sabado: 6,
      Domingo: 0,
    };
    return daysOfWeek[dia];
  }

  detectWhenIsByOrder(specialty: any) {
    const matchedSpecialty = specialty.find((item: any) => item.id === this.procedure.healthcareservice_id && item.byOrder === true);
    this.isByOrder = matchedSpecialty ? true : false;
  }

  generateTimeSlots(dayOfWeek: number, startHour: string, endHour: string, slotDuration: number): TimeSlot[] {
    let slots: TimeSlot[] = [];

    let startTime = new Date(`1970-01-01T${startHour}:00`);
    const endTime = new Date(`1970-01-01T${endHour}:00`);

    while (startTime < endTime) {
      let endTimeSlot = new Date(startTime.getTime() + slotDuration);

      if (endTimeSlot <= endTime) {
        slots.push({
          dayOfWeek,
          timeStart: startTime.toTimeString().slice(0, 5),
          timeEnd: endTimeSlot.toTimeString().slice(0, 5),
        });
      }

      startTime = endTimeSlot;
    }

    return slots;
  }

  private getPatient() {
    const patientId = this.getUserLogged().id;
    this.patientService.showNew(patientId).subscribe({
      next: (patient) => {
        this.patient = patient;
      },
    });
  }

  getOrganization() {
    this.locationService.listNew().subscribe({
      next: (organizations: any[]) => {
        this.organizations = organizations;
      },
      error: (err) => {
        console.log('Falha ao buscar as organizações', err);
      },
    });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      const dayOfWeek = cellDate.getDay();
      const dayName = this.getDayName(dayOfWeek);
      const hasSlots = this.weeklySchedule[dayName] && this.weeklySchedule[dayName].length > 0;
      return hasSlots ? 'available-slot' : '';
    }
    return '';
  };

  saveSchedule() {
    if (!this.canSaveSchedule()) return;

    this.isSavingSchedule = true;
    this.isByOrder ? this.addScheduleByOrder() : this.addSchedule();
  }

  addSchedule() {
    const data = {
      name: this.procedure.name,
      specialty: this.procedure.specialty,
      specialty_code: this.procedure.healthcareservice_id,
      value: this.procedure.price_high.toString(),
      type: this.procedure.serviceType!,
      active: true,
      servicecategory_name: this.procedure.type,
      organization: {
        name: this.schedule?.organization?.name ?? this.organization.name,
        organization_id: this.schedule?.organization?.organization_id ?? this.organization.organization_id,
      },
      practitioner: this.practitioner,
      patient: this.patientLogged,
      status: 'planned',
      appointment: '12344',
      startDate: this.datetime,
      byOrderInfo: {
        isByOrder: false,
        startServiceHour: this.datetime,
      },
      paymentMethod: this.schedule.paymentMethod,
      scheduling_method: this.schedule.scheduling_method,
      health_plan: {
        patient_name: this.patientLogged.name,
        health_plan_name: this.schedule.health_plan_name,
        agreement: this.schedule.agreement,
        card_number: this.schedule.health_plan_card,
        birth_date: this.patientLogged.birthDate,
      },
      content: this.uploadedFiles.map(function (file) {
        return {
          document: file.base64,
          name: file.name,
        };
      }),
      patient_details: {
        patient_id: this.patient.patient_id,
        phone: this.patient.mobile,
        email: this.patient.email,
        gender: this.patient.gender,
        birthDate: this.patient.birthDate,
      },
    };

    if (this.procedure.serviceDealer == 'Organização') {
      data.practitioner = {
        name: this.organization.name,
        practitioner_id: this.organization.organization_id,
      };
    }

    this.encounterService.createNew(data).subscribe({
      next: (encounter: Encounter) => {
        this.submittedSchedule = true;
        this.successSchedule = true;

        this.openSnackBar('Agendamento Realizado com sucesso. Agora, Realize seu pagamento', 'success');
        this.dialogRef.closeAll();
        this.router.navigate(['/patient-new/agenda/status']);
      },
      error: (error: HttpErrorResponse) => {
        this.messageError = error.error?.message || 'Falha ao realizar agendamento. Tente novamente.';

        this.submittedSchedule = true;
        this.successSchedule = false;
        this.isSavingSchedule = false;
      },
    });
  }

  getPaymentText() {
    let text = '';
    if (this.type == 'Consulta') {
      text = `Valor da consulta <b>R$ ${this.procedure?.price_high}.</b> `;
    }
    if (this.type == 'Exame') {
      text = `Valor da consulta <b>R$ ${this.procedure?.price_high}.</b> `;
    }
    return text;
  }

  addScheduleByOrder() {
    const data = {
      name: this.procedure.name,
      specialty: this.procedure.specialty,
      specialty_code: this.procedure.healthcareservice_id,
      value: this.procedure.price_high.toString(),
      type: this.procedure.serviceType!,
      active: true,
      servicecategory_name: this.procedure.type,
      organization: {
        name: this.schedule?.organization?.name ?? this.organization.name,
        organization_id: this.schedule?.organization?.organization_id ?? this.organization.organization_id,
      },
      practitioner: this.practitioner,
      patient: this.patientLogged,
      status: 'planned',
      appointment: '12344',
      startDate: this.datetime,
      byOrderInfo: {
        isByOrder: true,
        startServiceHour: this.byOrderStart,
      },
      scheduling_method: this.schedule.scheduling_method,
      paymentMethod: this.schedule.paymentMethod,
      health_plan: {
        patient_name: this.patientLogged.name,
        health_plan_name: this.schedule.health_plan_name,
        agreement: this.schedule.agreement,
        card_number: this.schedule.health_plan_card,
        birth_date: this.patientLogged.birthDate,
      },
      content: this.uploadedFiles.map(function (file) {
        return {
          document: file.base64,
          name: file.name,
        };
      }),
      patient_details: {
        patient_id: this.patient.patient_id,
        phone: this.patient.mobile,
        email: this.patient.email,
        gender: this.patient.gender,
        birthDate: this.patient.birthDate,
      },
    };

    if (this.procedure.serviceDealer == 'Organização') {
      data.practitioner = {
        name: this.organization.name,
        practitioner_id: this.organization.organization_id,
      };
    }

    this.encounterService.createNew(data).subscribe({
      next: (encounter: Encounter) => {
        this.submittedSchedule = true;
        this.successSchedule = true;
        this.router.navigate(['/patient-new/agenda/status']);
        this.openSnackBar('Agendamento Realizado com sucesso. Agora, Realize seu pagamento', 'success');
        this.dialogRef.closeAll();
      },
      error: (error: HttpErrorResponse) => {
        this.messageError = error.error?.message || 'Falha ao realizar agendamento. Tente novamente.';

        this.submittedSchedule = true;
        this.successSchedule = false;
        this.isSavingSchedule = false;
      },
    });
  }

  textForProcedureByOrder() {
    let text = '';
    if (this.type == 'Consulta') {
      text = `O atendimento desse procedimento é realizado por ordem de chegada.</b> `;
    }
    if (this.type == 'Exame') {
      text = `O atendimento desse procedimento é realizado por ordem de chegada.</b> `;
    }
    return text;
  }

  hasError(field: string) {
    if ((field == 'patient' && !this.schedule.patient) || (field == 'time' && this.schedule.dateStart == '')) {
      return true;
    }
    return false;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false; // Remover o estado de "arrastar ativo"

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const files = Array.from(event.dataTransfer.files);

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            const base64 = reader.result.toString().split(',')[1]; // Pegar apenas o conteúdo base64
            this.uploadedFiles.push({ name: file.name, base64 });
          }
        };
        reader.readAsDataURL(file); // Ler o arquivo como Data URL
      });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      Array.from(input.files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            const base64 = reader.result.toString();
            this.uploadedFiles.push({ name: file.name, base64 });
          }
        };
        reader.readAsDataURL(file); // Ler o arquivo como Data URL
      });
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  close() {
    this.dialogRef.closeAll();
  }

  formatPhone(value?: string): string {
    if (!value) return '';
    let digits = String(value).replace(/\D/g, '');
    if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
    if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return value;
  }

  get contactPhone(): string | undefined {
    return this.patient?.mobile || this.patientLogged?.phone;
  }
}
